import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ActivityGroup } from '../../api/activity-client';
import { getHandoffLog } from '../../api/handoff-client';
import {
  HandoffLogEntry,
  HandoffLogResponse,
} from '../../interfaces/handoff-interfaces';
import { ActivityEntryRow, Chip, List } from '../activity/activity-entry-row';
import { palette, spacing, typography } from '../../style-utils/theme';
import { Caption, GhostButton, SkeletonRow } from './iq-shared.styled';
import { Tab, TabRow } from './iq-handoff.styled';

/* ═══════════ THE SHIFT, ENTRY BY ENTRY ═══════════
 *
 * Owner instruction, 2026-09-11: a handover without Lobbero IQ must be "a list
 * of things that happened ... everything, every log in detail", and an IQ
 * handover carries a button to the same list. A local run that day logged five
 * entries on a shift and found four of them nowhere in the handover — the
 * non-IQ record was three rolled-up flags.
 *
 * WHAT IT IS: every event in the handover's frozen window, oldest first, each
 * row the activity feed's own row with EVERY note shown (`detailed`). Read LIVE
 * from the log (GET /shifts/handoff/:id/log) — the caption says so, and a row
 * edited after the handover carries a tag saying that too, because the rest of
 * the card is a frozen record and this list is not.
 *
 * NO "OPEN" ON A ROW. This list renders inside the handover modal — full screen
 * on a takeover — and raising the record modal over it is the stacking trap
 * modal-portal.tsx exists for. Every field a record view would add is already
 * on the detailed row.
 */

const PAGE = 50;

type TabKey = ActivityGroup | 'all';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
  /* Names and free text: one long word must not set the column width. */
  overflow-wrap: anywhere;
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Note = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

const Skeletons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

export interface HandoffLogProps {
  handoffId: string;
  /** The handover window's zone — the BUILDING's clock, never the reader's. */
  tz: string;
  title?: string;
}

export const HandoffLog: React.FC<HandoffLogProps> = ({
  handoffId,
  tz,
  title = 'Everything logged during the shift',
}) => {
  const [group, setGroup] = useState<TabKey>('all');
  const [entries, setEntries] = useState<HandoffLogEntry[]>([]);
  const [meta, setMeta] = useState<HandoffLogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // One request at a time: a tab pressed mid-load must not let the older answer
  // land on top of the newer question.
  const inFlight = useRef<AbortController | null>(null);

  const load = useCallback(
    async (offset: number) => {
      inFlight.current?.abort();
      const ac = new AbortController();
      inFlight.current = ac;
      setLoading(true);
      setError(null);
      try {
        const res = await getHandoffLog(
          handoffId,
          {
            limit: PAGE,
            offset,
            group: group === 'all' ? undefined : [group],
          },
          { signal: ac.signal }
        );
        if (ac.signal.aborted) return;
        setMeta(res);
        setEntries(prev => (offset === 0 ? res.entries : prev.concat(res.entries)));
      } catch (e) {
        if (ac.signal.aborted) return;
        // Never rendered as "nothing was logged": an error that looks like an
        // empty shift is how a broken screen stays broken (RC-10).
        setError(
          e instanceof Error ? e.message : 'Could not read the log for this handover.'
        );
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    },
    [handoffId, group]
  );

  useEffect(() => {
    setEntries([]);
    void load(0);

    return () => inFlight.current?.abort();
  }, [load]);

  // All first, then only the tabs that hold something: on a handover an empty
  // "Corrections (0)" tab is a control with nothing behind it.
  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: meta?.groupTotal ?? 0 },
    ...(meta?.groups || [])
      .filter(g => g.count > 0)
      .map(g => ({ key: g.key, label: g.label, count: g.count })),
  ];
  const changed = entries.filter(e => e.changedSinceHandover).length;
  const shown = meta?.totalCapped ? `${meta.total}+` : String(meta?.total ?? 0);

  return (
    <Wrap aria-busy={loading}>
      <Title>{title}</Title>

      {meta && meta.groupTotal > 0 ? (
        <TabRow role="tablist" aria-label="Filter the shift log">
          {tabs.map(t => (
            <Tab
              key={t.key}
              type="button"
              role="tab"
              aria-selected={group === t.key}
              $active={group === t.key}
              onClick={() => setGroup(t.key)}
            >
              {`${t.label} (${t.count})`}
            </Tab>
          ))}
        </TabRow>
      ) : null}

      {error ? (
        <>
          <Note role="alert">{error}</Note>
          <div>
            <GhostButton type="button" onClick={() => void load(0)}>
              Try again
            </GhostButton>
          </div>
        </>
      ) : !meta ? (
        <Skeletons aria-hidden="true">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </Skeletons>
      ) : entries.length === 0 ? (
        <Note>
          {group === 'all'
            ? 'Nothing was logged during this shift.'
            : 'Nothing of this kind was logged during this shift.'}
        </Note>
      ) : (
        <List aria-label="Shift log, oldest first">
          {entries.map((e, i) => (
            <ActivityEntryRow
              key={`${e.ref.source}:${e.ref.id}:${e.direction}:${i}`}
              entry={e}
              tz={tz}
              index={i}
              pageSize={PAGE}
              detailed
              extraChips={
                e.changedSinceHandover ? (
                  <Chip tone="open">Changed since the handover</Chip>
                ) : null
              }
            />
          ))}
        </List>
      )}

      {meta && entries.length > 0 ? (
        <Foot>
          {/* The end of the list is STATED, so "that is everything" and "it
              stopped loading" never look the same. */}
          <Caption>{`Showing ${entries.length} of ${shown}`}</Caption>
          {meta.hasMore ? (
            <GhostButton
              type="button"
              disabled={loading}
              onClick={() => void load(entries.length)}
            >
              {loading ? 'Loading…' : 'Show more'}
            </GhostButton>
          ) : null}
        </Foot>
      ) : null}

      <Caption>
        {`Read live from the log for the hours of this shift${
          changed
            ? ` — ${changed} ${
                changed === 1 ? 'entry was' : 'entries were'
              } changed after the handover was written`
            : ''
        }.`}
      </Caption>
    </Wrap>
  );
};
